import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useVideoSession } from "../../hooks/useVideoSession.js";
import WaitingRoom          from "../../components/patient/WaitingRoom.jsx"
import VideoRoom            from "../../components/patient/VideoRoom.jsx";
import Spinner              from "../../components/Spinner.jsx";

/**
 * Lobby page — entry point from the patient's email invite link.
 *
 * URL: /lobby?sessionId=<uuid>
 *
 * State machine:
 *   idle → joining → waiting → in-call → ended
 */
export default function Lobby() {
  const [params]   = useSearchParams();
  const sessionId  = params.get("sessionId");

  const {
    session,
    status,
    error,
    joinLobby,
    leaveCall,
  } = useVideoSession();

  // Kick off the join as soon as the component mounts
  useEffect(() => {
    if (sessionId && status === "idle") {
      joinLobby(sessionId);
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── No session ID in URL ──────────────────────────────────────────────
  if (!sessionId) {
    return (
      <div className="page-center">
        <div className="error-card">
          <span className="error-icon" aria-hidden="true">🔗</span>
          <h2>Invalid invite link</h2>
          <p>This link is missing a session ID. Please check your email and try again.</p>
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="page-center">
        <div className="error-card">
          <span className="error-icon" aria-hidden="true">⚠️</span>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => joinLobby(sessionId)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ── Status-driven rendering ───────────────────────────────────────────
  switch (status) {
    case "idle":
    case "joining":
      return (
        <div className="page-center">
          <Spinner message="Connecting to your appointment…" />
        </div>
      );

    case "waiting":
      return <WaitingRoom session={session} onLeave={leaveCall} />;

    case "in-call":
      return (
        <VideoRoom
          patientName={session?.patientName}
          doctorName={session?.doctorName}
          onLeave={leaveCall}
        />
      );

    case "ended":
      return (
        <div className="page-center">
          <div className="ended-card">
            <span className="ended-icon" aria-hidden="true">✅</span>
            <h2>Your visit has ended</h2>
            <p>Thank you for using CareLink. Have a great day!</p>
          </div>
        </div>
      );

    default:
      return null;
  }
}
